import React, { useState, useEffect } from 'react';
import { 
  Text, 
  Stack, 
  Separator, 
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  IconButton,
  Pivot,
  PivotItem
} from '@fluentui/react';
import { authenticatedFetch } from '../utils/authApi';
import { useAdminAccess } from '../components/Auth/AuthProvider';
import { 
  ChatRegular,
  BookRegular,
  DocumentRegular,
  SearchRegular,
  CloudAddRegular,
  SettingsRegular,
  MoneyRegular,
  CloudRegular,
  BugRegular,
  PlayRegular,
  LightbulbRegular,
  CheckmarkCircleRegular,
  InfoRegular,
  HistoryRegular,
  NewsRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ShieldRegular
} from '@fluentui/react-icons';
import styles from './HelpPage.module.css';
import NewsSection from '../components/News/NewsSection';
import { FeatureGrid } from '../components/Help';
import AdminGuide from '../components/Help/AdminGuide';

interface HelpSection {
  id: string;
  title: string;
  icon: string;
  description: string;
  content: any;
  lastModified: string;
}

type ActiveTab = 'features' | 'admin';

interface HelpFeaturesData {
  features: any[];
  categories: Record<string, any>;
  quickStartSteps: any[];
  metadata: any;
}

const HelpPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('features');
  const [selectedSection, setSelectedSection] = useState<string>('news');
  const [helpSections, setHelpSections] = useState<HelpSection[]>([]);
  const [helpFeaturesData, setHelpFeaturesData] = useState<HelpFeaturesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const { isAdmin, isAdminLoading } = useAdminAccess();

  useEffect(() => {
    loadHelpFeatures();
  }, []);

  const loadHelpFeatures = async () => {
    try {
      setLoading(true);
      
      // Fetch help features from Cosmos
      const response = await authenticatedFetch('/api/help/features');
      const data = await response.json();
      
      if (data.success) {
        setHelpFeaturesData({
          features: data.features || [],
          categories: data.categories || {},
          quickStartSteps: data.quickStartSteps || [],
          metadata: data.metadata || {}
        });
      } else {
        console.error('Failed to load help features');
        // Set empty default
        setHelpFeaturesData({
          features: [],
          categories: {},
          quickStartSteps: [],
          metadata: {}
        });
      }
    } catch (err) {
      console.error('Error loading help features:', err);
      // Set empty default on error
      setHelpFeaturesData({
        features: [],
        categories: {},
        quickStartSteps: [],
        metadata: {}
      });
    } finally {
      setLoading(false);
    }
  };

  // Legacy function kept for potential backward compatibility
  const loadHelpSections = async () => {
    try {
      // Add the News section as the first item
      const newsSection: HelpSection = {
        id: 'news',
        title: 'News & Updates',
        icon: 'NewsRegular',
        description: 'Latest features, releases, and announcements',
        content: {},
        lastModified: new Date().toISOString()
      };
      
      const response = await authenticatedFetch('/api/help/sections');
      const data = await response.json();
      
      if (data.success) {
        setHelpSections([newsSection, ...data.sections]);
        const allSections = [newsSection, ...data.sections];
        if (!allSections.find((s: HelpSection) => s.id === selectedSection)) {
          setSelectedSection('news');
        }
      } else {
        setHelpSections([newsSection]);
        setSelectedSection('news');
      }
    } catch (err) {
      const newsSection: HelpSection = {
        id: 'news',
        title: 'News & Updates',
        icon: 'NewsRegular',
        description: 'Latest features, releases, and announcements',
        content: {},
        lastModified: new Date().toISOString()
      };
      setHelpSections([newsSection]);
      setSelectedSection('news');
      console.error('Error loading help sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const getIconComponent = (iconName: string): JSX.Element => {
    // Map icon names to actual icon components
    const iconMap: { [key: string]: JSX.Element } = {
      'NewsRegular': <NewsRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'PlayRegular': <PlayRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'ChatRegular': <ChatRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'BookRegular': <BookRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'DocumentRegular': <DocumentRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'SearchRegular': <SearchRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'CloudAddRegular': <CloudAddRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'SettingsRegular': <SettingsRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'MoneyRegular': <MoneyRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'CloudRegular': <CloudRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'BugRegular': <BugRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'LightbulbRegular': <LightbulbRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'CheckmarkCircleRegular': <CheckmarkCircleRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'InfoRegular': <InfoRegular style={{ fontSize: 24, color: '#0078d4' }} />,
      'HistoryRegular': <HistoryRegular style={{ fontSize: 24, color: '#0078d4' }} />
    };
    
    return iconMap[iconName] || <InfoRegular style={{ fontSize: 24, color: '#0078d4' }} />;
  };

  const renderSectionContent = (section: HelpSection): JSX.Element => {
    // Special handling for news section
    if (section.id === 'news') {
      return <NewsSection />;
    }
    
    const content = section.content;

    return (
      <Stack tokens={{ childrenGap: 20 }}>
        <Text variant="large" style={{ fontWeight: 600, color: '#1a253c' }}>
          {content.title}
        </Text>
        
        {content.description && (
          <Text>{content.description}</Text>
        )}

        {content.sections && content.sections.map((contentSection: any, index: number) => (
          <Stack key={index} tokens={{ childrenGap: 10 }}>
            {contentSection.title && (
              <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
                {contentSection.title}:
              </Text>
            )}
            
            {contentSection.type === 'success' ? (
              <MessageBar messageBarType={MessageBarType.success}>
                <Text><strong>{contentSection.content}</strong></Text>
              </MessageBar>
            ) : (
              <Stack tokens={{ childrenGap: 8 }}>
                {contentSection.content.split('\n').map((line: string, lineIndex: number) => (
                  line.trim() && (
                    <Text key={lineIndex}>
                      {line.startsWith('•') ? line : `${line}`}
                    </Text>
                  )
                ))}
              </Stack>
            )}
          </Stack>
        ))}

        {content.tips && content.tips.length > 0 && (
          <MessageBar messageBarType={MessageBarType.info}>
            <Text>
              <strong>Pro Tip:</strong> {content.tips[0]}
            </Text>
          </MessageBar>
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Stack horizontalAlign="center" verticalAlign="center" style={{ height: '400px' }}>
          <Spinner size={SpinnerSize.large} />
          <Text>Loading help content...</Text>
        </Stack>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <MessageBar messageBarType={MessageBarType.error}>
          {error}
        </MessageBar>
      </div>
    );
  }

  const selectedSectionData = helpSections.find(section => section.id === selectedSection);

  // Render the new feature-focused design
  const renderNewDesign = () => (
    <div className={styles.newDesignContainer}>
      {/* Feature Showcase */}
      {helpFeaturesData && <FeatureGrid data={helpFeaturesData as any} />}
      
      {/* News Section at the bottom */}
      <div className={styles.newsWrapper}>
        <div className={styles.newsSectionHeader}>
          <NewsRegular style={{ fontSize: 24, color: '#667eea' }} />
          <Text className={styles.newsSectionTitle}>News & Updates</Text>
        </div>
        <NewsSection />
      </div>
    </div>
  );

  // Render legacy sidebar design for old documentation
  const renderLegacyDesign = () => (
    <Stack horizontal className={styles.layout}>
      {/* Sidebar */}
      <Stack className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`} tokens={{ childrenGap: 8 }}>
        {!sidebarCollapsed && (
          <>
            <div className={styles.sidebarHeader}>
              <Text variant="large" style={{ fontWeight: 600, color: '#1a253c' }}>
                Categories
              </Text>
              <IconButton
                onClick={() => setSidebarCollapsed(true)}
                className={styles.collapseButton}
                ariaLabel="Collapse categories"
              >
                <ChevronLeftRegular style={{ fontSize: 16 }} />
              </IconButton>
            </div>
            
            <Separator />
            
            <Stack tokens={{ childrenGap: 4 }}>
              {helpSections.filter(s => s.id !== 'news').map((section) => (
                <div
                  key={section.id}
                  className={`${styles.sidebarItem} ${selectedSection === section.id ? styles.selected : ''}`}
                  onClick={() => setSelectedSection(section.id)}
                >
                  <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                    {getIconComponent(section.icon)}
                    <Stack tokens={{ childrenGap: 2 }}>
                      <Text variant="medium" style={{ fontWeight: selectedSection === section.id ? 600 : 500 }}>
                        {section.title}
                      </Text>
                      <Text variant="small" style={{ color: '#666', lineHeight: '16px' }}>
                        {section.description}
                      </Text>
                    </Stack>
                  </Stack>
                </div>
              ))}
            </Stack>
          </>
        )}
        
        {sidebarCollapsed && (
          <div className={styles.collapsedSidebar}>
            <IconButton
              onClick={() => setSidebarCollapsed(false)}
              className={styles.expandButton}
              ariaLabel="Expand categories"
            >
              <ChevronRightRegular style={{ fontSize: 16 }} />
            </IconButton>
          </div>
        )}
      </Stack>

      {/* Main Content */}
      <Stack className={styles.mainContent} tokens={{ childrenGap: 20 }}>
        {selectedSectionData && selectedSectionData.id !== 'news' && (
          <>
            <Stack horizontal tokens={{ childrenGap: 15 }} verticalAlign="center" className={styles.sectionHeader}>
              {getIconComponent(selectedSectionData.icon)}
              <Stack tokens={{ childrenGap: 5 }}>
                <Text variant="xxLarge" style={{ fontWeight: 600, color: '#1a253c' }}>
                  {selectedSectionData.title}
                </Text>
                <Text variant="medium" style={{ color: '#666' }}>
                  {selectedSectionData.description}
                </Text>
              </Stack>
            </Stack>
            <Separator />
            <div className={styles.sectionContent}>
              {renderSectionContent(selectedSectionData)}
            </div>
          </>
        )}
      </Stack>
    </Stack>
  );

  return (
    <div className={styles.container}>
      {/* Main Header */}
      <div className={styles.mainHeader}>
        <Text variant="xxLarge" style={{ fontWeight: 700, color: '#1a253c' }}>
          Help Center
        </Text>
        <Text variant="large" style={{ color: '#666', marginTop: 5 }}>
          Learn how to use the platform effectively
        </Text>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabContainer}>
        <Pivot
          selectedKey={activeTab}
          onLinkClick={(item) => setActiveTab(item?.props.itemKey as ActiveTab)}
          styles={{
            root: { marginBottom: 20 },
            link: { fontSize: 15, fontWeight: 500 },
            linkIsSelected: { fontWeight: 600 }
          }}
        >
          <PivotItem headerText="📚 Feature Guide" itemKey="features" />
          {isAdmin && !isAdminLoading && (
            <PivotItem headerText="🛡️ Admin Guide" itemKey="admin" />
          )}
        </Pivot>
      </div>

      {/* Content Area */}
      {activeTab === 'features' && renderNewDesign()}
      {activeTab === 'admin' && isAdmin && <AdminGuide />}
    </div>
  );
};

export default HelpPage;
